<template>
    <div :class="`phone-number phone-number-align-${align}`">
        <label for="phone-number" class="field-label">
            {{ fieldPhone }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <div :class="['input-wrapper', { 'error': shouldShowError, 'filled': nationalNumber }]">
            <div class="country-select">
                <span class="country-flag">{{ currentFlag }}</span>
                <span class="country-dial">{{ currentDialCode }}</span>
                <span class="country-caret">▾</span>
                <select
                    class="country-native"
                    :value="selectedCountry"
                    :aria-label="fieldPhone"
                    @change="onCountryChange($event)"
                >
                    <option v-for="c in countryOptions" :key="c.iso2" :value="c.iso2">
                        {{ countryFlag(c.iso2) }} {{ c.name }} (+{{ c.dialCode }})
                    </option>
                </select>
            </div>
            <input
                type="tel"
                id="phone-number"
                name="phone-number"
                :value="nationalNumber"
                @input="onInput($event)"
                @focus="onFocus"
                @blur="onBlur"
                :class="{ 'has-value': nationalNumber }"
            />
            <label for="phone-number" class="floating-label">{{ masked }}</label>
            <div class="input-border"></div>
        </div>
        <div v-if="shouldShowError" class="error-message">
            {{ fieldPhone }} alanı zorunludur
        </div>
    </div>
</template>

<script>
import {
    COUNTRY_CODES,
    DEFAULT_COUNTRY_ISO,
    countryFlag,
    getCountryByIso,
    getDialCode,
} from "../../utils/countryCodes";

// Uzun dial code'lar önce denenmeli, aksi halde "+994" yanlışlıkla "+9..." ile eşleşebilir
const COUNTRIES_BY_DIAL_LENGTH = COUNTRY_CODES.slice().sort(
    (a, b) => b.dialCode.length - a.dialCode.length
);

/**
 * "+905551234567" gibi birleşik değeri ülke + ulusal numaraya ayırır.
 * matched: değerin içinde açıkça bir ülke kodu bulunduğunu belirtir.
 * @returns {{ iso: string, national: string, matched: boolean }}
 */
function parsePhoneString(phoneStr, fallbackIso) {
    const raw = (phoneStr || "").toString().trim();
    if (!raw) return { iso: fallbackIso, national: "", matched: false };

    const digits = raw.replace(/\D/g, "");
    if (raw.charAt(0) !== "+") {
        return { iso: fallbackIso, national: digits, matched: false };
    }

    for (let i = 0; i < COUNTRIES_BY_DIAL_LENGTH.length; i++) {
        const country = COUNTRIES_BY_DIAL_LENGTH[i];
        if (digits.indexOf(country.dialCode) === 0) {
            // +1 (US/CA) ve +7 (RU/KZ) gibi paylaşılan kodlarda mevcut seçim korunur
            const fallback = getCountryByIso(fallbackIso);
            const iso = fallback && fallback.dialCode === country.dialCode
                ? fallback.iso2
                : country.iso2;
            return { iso: iso, national: digits.substring(country.dialCode.length), matched: true };
        }
    }
    return { iso: fallbackIso, national: digits, matched: false };
}

/** TR mobil format: 5xx xxx xx xx */
function formatTRNational(digits) {
    let d = digits;
    if (d.charAt(0) === "0") d = d.substring(1);
    d = d.substring(0, 10);
    if (d.length > 8) {
        return d.substring(0, 3) + " " + d.substring(3, 6) + " " + d.substring(6, 8) + " " + d.substring(8);
    }
    if (d.length > 6) {
        return d.substring(0, 3) + " " + d.substring(3, 6) + " " + d.substring(6);
    }
    if (d.length > 3) {
        return d.substring(0, 3) + " " + d.substring(3);
    }
    return d;
}

export default {
    name: "FormPhoneNumber",
    props: {
        fieldPhone: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        masked: {
            type: String,
            required: true,
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({ phoneNumber: "" }),
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        fieldId: {
            type: String,
            required: true,
        },
        defaultCountryCode: {
            type: String,
            default: DEFAULT_COUNTRY_ISO,
        },
    },
    data() {
        const initialIso = this.defaultCountryCode || DEFAULT_COUNTRY_ISO;
        const incoming = (typeof this.value === "object" && this.value !== null)
            ? this.value.phoneNumber || ""
            : "";
        const parsed = parsePhoneString(incoming, initialIso);
        return {
            selectedCountry: parsed.iso || initialIso,
            nationalNumber: this.formatNational(parsed.national, parsed.iso || initialIso),
            // Kullanıcı ülkeyi elle seçtiyse gelen değerler bunu ezmemeli
            userSelectedCountry: parsed.matched,
        };
    },
    computed: {
        currentFlag() {
            return countryFlag(this.selectedCountry);
        },
        currentDialCode() {
            return getDialCode(this.selectedCountry);
        },
        fullPhoneNumber() {
            const digits = (this.nationalNumber || "").replace(/\D/g, "");
            if (!digits) return "";
            return getDialCode(this.selectedCountry) + digits;
        },
        countryOptions() {
            return COUNTRY_CODES;
        },
        hasValidationError() {
            return this.isRequired && !this.fullPhoneNumber;
        },
        shouldShowError() {
            return this.showError && this.hasValidationError;
        }
    },
    watch: {
        value: {
            handler(newValue) {
                const incoming = (typeof newValue === "object" && newValue !== null)
                    ? newValue.phoneNumber || ""
                    : "";
                // Parent kendi emit ettiğimiz değeri geri yazdığında state'i sıfırlamayalım
                if (incoming === this.fullPhoneNumber) return;

                const parsed = parsePhoneString(incoming, this.selectedCountry);
                // Ülke yalnızca gelen değerde açık bir ülke kodu varsa değişir
                if (parsed.matched) {
                    this.selectedCountry = parsed.iso;
                    this.userSelectedCountry = true;
                }
                this.nationalNumber = this.formatNational(parsed.national, this.selectedCountry);
                // Slug prefill gibi ülke kodsuz gelen değerlerde parent'ı normalize edilmiş
                // (+90...) hale çekiyoruz; guard sayesinde döngüye girmiyor
                if (incoming !== this.fullPhoneNumber) {
                    this.emitValues();
                }
            },
            deep: true,
        },
        defaultCountryCode(newVal) {
            if (!this.userSelectedCountry && !this.nationalNumber) {
                this.selectedCountry = newVal || DEFAULT_COUNTRY_ISO;
            }
        },
    },
    methods: {
        countryFlag,
        formatNational(raw, iso2) {
            const digits = (raw || "").replace(/\D/g, "");
            if ((iso2 || "").toUpperCase() === "TR") {
                return formatTRNational(digits);
            }
            return digits.substring(0, 15);
        },
        onInput(event) {
            const el = event && event.target ? event.target : null;
            const raw = el ? el.value : this.nationalNumber;
            const formatted = this.formatNational(raw, this.selectedCountry);
            this.nationalNumber = formatted;
            // Vue verisi değişmediğinde (geçersiz karakter, fazla hane) DOM'u elle senkronla
            if (el && el.value !== formatted) {
                el.value = formatted;
            }
            this.emitValues();
        },
        onCountryChange(event) {
            const iso = event && event.target ? event.target.value : this.selectedCountry;
            this.selectedCountry = iso;
            this.userSelectedCountry = true;
            this.nationalNumber = this.formatNational(
                (this.nationalNumber || "").replace(/\D/g, ""),
                iso
            );
            this.emitValues();
        },
        emitValues() {
            const phoneData = {
                phoneNumber: this.fullPhoneNumber,
                fieldPhone: this.fieldPhone,
                masked: this.masked,
            };

            console.log('🔍 Phone validation:', {
                fieldId: this.fieldId,
                isRequired: this.isRequired,
                fullPhoneNumber: this.fullPhoneNumber,
                nationalNumber: this.nationalNumber,
                selectedCountry: this.selectedCountry,
                hasValidationError: this.hasValidationError,
                shouldShowError: this.shouldShowError
            });

            this.$emit("input", phoneData);
            this.$emit("validate", {
                fieldId: this.fieldId,
                hasError: this.hasValidationError,
            });
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(phoneData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                const parsed = parsePhoneString(parsedData.phoneNumber, this.selectedCountry);
                if (parsed.matched) {
                    this.selectedCountry = parsed.iso;
                    this.userSelectedCountry = true;
                }
                this.nationalNumber = this.formatNational(parsed.national, this.selectedCountry);
            }
        },
        onFocus(event) {
            event.target.parentElement.classList.add('focused');
        },
        onBlur(event) {
            event.target.parentElement.classList.remove('focused');
        }
    },
    mounted() {
        console.log('🔴 PhoneNumber mounted:', {
            fieldId: this.fieldId,
            isRequired: this.isRequired,
            value: this.value,
            defaultCountryCode: this.defaultCountryCode,
            selectedCountry: this.selectedCountry,
            nationalNumber: this.nationalNumber,
            fieldPhone: this.fieldPhone
        });

        const hasSlugValue = this.value && typeof this.value === 'object' && this.value.phoneNumber;

        if (!hasSlugValue) {
            this.loadValues();
        }

        this.emitValues();
    }
};
</script>

<style lang="scss" scoped>
/* Base Phone Number Styles */
.phone-number {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-wrapper {
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        min-width: 0; // Flex item için minimum genişlik
        display: flex;
        align-items: stretch;

        .country-select {
            position: relative;
            display: flex;
            align-items: center;
            gap: 0.25rem;
            flex: 0 0 auto;
            min-width: 84px;
            padding: 0 clamp(0.5rem, 1.2vw, 0.625rem);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-right: none;
            border-radius: 0.375rem 0 0 0.375rem;
            background: rgba(248, 250, 252, 0.7);
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            cursor: pointer;
            user-select: none;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

            .country-flag {
                font-size: 1.1em;
                line-height: 1;
            }

            .country-dial {
                font-weight: 600;
                white-space: nowrap;
            }

            .country-caret {
                margin-left: 0.125rem;
                font-size: 0.7em;
                opacity: 0.6;
            }

            // Native select tetikleyicinin üzerini tamamen kaplar; açılır listeyi tarayıcı
            // çizdiği için hiçbir overflow/transform onu kesemez
            .country-native {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                border: none;
                background: transparent;
                color: transparent;
                opacity: 0;
                cursor: pointer;
                appearance: none;
                -webkit-appearance: none;
                font-size: 1rem; // iOS'ta odaklanınca sayfayı yakınlaştırmaması için

                &:focus {
                    outline: none;
                }
            }

            &:focus-within {
                border-color: var(--primary, #6366f1);
            }
        }

        input {
            width: 100%;
            padding: clamp(0.6rem, 1.5vw, 0.75rem) clamp(0.6rem, 1.2vw, 0.75rem);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.4;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0 0.375rem 0.375rem 0;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: inherit;
            backdrop-filter: blur(10px);
            border-left: none;
            
            &::placeholder {
                color: transparent;
            }
            
            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 
                    0 0 0 3px rgba(99, 102, 241, 0.1),
                    0 4px 12px rgba(0, 0, 0, 0.05);
                transform: translateY(-1px);
            }
            
            &:hover:not(:focus) {
                border-color: rgba(107, 114, 128, 0.4);
                background: rgba(255, 255, 255, 0.85);
            }
        }

        .floating-label {
            position: absolute;
            left: calc(84px + clamp(0.6rem, 1.2vw, 0.75rem));
            top: 50%;
            transform: translateY(-50%);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            color: rgba(107, 114, 128, 0.7);
            pointer-events: none;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            background: transparent;
            padding: 0 0.25rem;
            font-weight: 500;
        }

        .input-border {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            border-radius: 1px;
            transform: scaleX(0);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        &.focused .floating-label,
        &.filled .floating-label,
        input:focus ~ .floating-label,
        input.has-value ~ .floating-label {
            top: 0;
            transform: translateY(-50%);
            font-size: clamp(0.7rem, 1.6vw, 0.8rem);
            color: var(--primary, #6366f1);
            font-weight: 600;
            background: rgba(255, 255, 255, 0.9);
        }

        &.focused .input-border,
        input:focus ~ .input-border {
            transform: scaleX(1);
        }

        &.error {
            input {
                border-color: #ef4444;
                background: rgba(254, 242, 242, 0.8);
                
                &:focus {
                    border-color: #ef4444;
                    box-shadow: 
                        0 0 0 3px rgba(239, 68, 68, 0.1),
                        0 4px 12px rgba(239, 68, 68, 0.1);
                }
            }
            
            .floating-label {
                color: #ef4444;
            }
            
            .input-border {
                background: #ef4444;
            }
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.375rem, 1vw, 0.5rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        
        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, input sağda */
.phone-number-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .input-wrapper {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
    }

    .input-wrapper {
        width: 100%;
        }
    }
}

/* Right Alignment - Label solda sağa yaslanmış, input sağda */
.phone-number-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .input-wrapper {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            text-align: left;
    }

    .input-wrapper {
        width: 100%;
    }
}
}

/* Center/Top Alignment (Default) */
.phone-number-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .phone-number {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
    }

    .input-wrapper {
        input {
                padding: clamp(0.75rem, 2vw, 0.875rem) clamp(0.625rem, 1.5vw, 0.75rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                border-radius: 0.5rem;
            }
            
            .floating-label {
                left: calc(84px + clamp(0.625rem, 1.5vw, 0.75rem));
                font-size: clamp(0.875rem, 2vw, 0.9rem);

                &.focused,
                &.filled {
                    font-size: clamp(0.75rem, 1.8vw, 0.8rem);
                }
            }
        }

        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
    }
}

@media (max-width: 480px) {
    .phone-number {
        margin: 0.25rem auto;
        padding: 0.5rem;

        .input-wrapper {
            input {
                padding: 0.75rem 0.625rem;
                font-size: 0.875rem;
            }

            .floating-label {
                left: calc(84px + 0.625rem);
                font-size: 0.875rem;
                
                &.focused,
                &.filled {
                    font-size: 0.75rem;
                }
            }
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .phone-number,
    .input-wrapper,
    .input-wrapper input,
    .floating-label,
    .input-border {
        transition: none;
    }
    
    .input-wrapper input:focus {
        transform: none;
    }
}

/* High Performance Mode */
.input-wrapper {
    will-change: transform;
    
    input {
        will-change: transform, box-shadow;
    }
    
    .floating-label {
        will-change: transform, color, font-size;
        }
    
    .input-border {
        will-change: transform;
    }
}
</style>